import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { X, Save, Info, AlertCircle } from 'lucide-react';
import { apiGet, apiPatch } from '../utils/apiClient';
import { ConfirmationDrawer } from './ConfirmationDrawer';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useModalFocus } from '../hooks/useModalFocus';

export function PreferencesDrawer({ isOpen, onClose }) {
  const countryOptions = useMemo(() => ([
    { code: 'AU', name: 'Australia' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IN', name: 'India' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'BR', name: 'Brazil' },
    { code: 'AR', name: 'Argentina' },
    { code: 'SG', name: 'Singapore' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'PH', name: 'Philippines' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'MX', name: 'Mexico' },
    { code: 'ES', name: 'Spain' },
    { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' },
    { code: 'IT', name: 'Italy' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'DK', name: 'Denmark' },
    { code: 'JP', name: 'Japan' },
    { code: 'KR', name: 'South Korea' },
    { code: 'CN', name: 'China' },
  ]), []);

  const [preferences, setPreferences] = useState(null);
  const [countryInput, setCountryInput] = useState('');
  const [newStaple, setNewStaple] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialPreferences, setInitialPreferences] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [householdMeta, setHouseholdMeta] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const summaryRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLoadError('');
        const [prefs, household] = await Promise.all([
          apiGet('/user/preferences'),
          apiGet('/households/me').catch(() => null),
        ]);
        if (!cancelled) {
          setPreferences(prefs);
          setInitialPreferences(prefs);
          setCountryInput(prefs.countryCode || '');
          setHouseholdMeta(household);
        }
      } catch (e) {
        console.warn('Failed to load user preferences:', e);
        if (!cancelled) {
          setLoadError('Unable to load preferences right now. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const ownerUid = householdMeta?.ownerUid;
  const isOwner = ownerUid && user?.uid ? ownerUid === user.uid : true;

  const panelRef = useRef(null);
  const titleId = useId();
  // Paused while the confirmation drawer is open on top, so that one keeps focus instead.
  useModalFocus({ isOpen, panelRef, onClose, paused: confirmOpen });

  if (!isOpen) return null;

  const focusSummary = () => {
    requestAnimationFrame(() => {
      summaryRef.current?.focus();
    });
  };

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const currentPrefs =
    preferences || {
      weekStartDay: 'monday',
      includedMeals: { breakfast: true, lunch: true, dinner: true },
      countryCode: '',
      staplesEnabled: true,
      staples: [],
      defaultServes: { breakfast: 2, lunch: 2, dinner: 4 },
    };

  const weekStartChanged =
    !!initialPreferences &&
    !!preferences &&
    initialPreferences.weekStartDay !== preferences.weekStartDay;

  const savePreferences = async () => {
    if (!preferences) {
      onClose();
      return;
    }
    try {
      setSaving(true);
      setSaveError('');
      const updated = await apiPatch('/user/preferences', preferences);
      setPreferences(updated);
      setCountryInput(updated.countryCode || '');
      if (weekStartChanged) {
        navigate('/MealPlan');
      }
      setConfirmOpen(false);
      onClose();
    } catch (err) {
      console.warn('Failed to save preferences:', err);
      setSaveError('Could not save preferences right now. Please try again.');
      focusSummary();
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});
    if (!preferences) {
      onClose();
      return;
    }
    if (weekStartChanged) {
      setConfirmOpen(true);
      return;
    }
    void savePreferences();
  };

  const handleMealToggle = (meal) => {
    setPreferences((prev) => {
      const base =
        prev || {
          weekStartDay: 'monday',
          includedMeals: { breakfast: true, lunch: true, dinner: true },
          countryCode: '',
          staplesEnabled: true,
          staples: [],
          defaultServes: { breakfast: 2, lunch: 2, dinner: 4 },
        };
      return {
        ...base,
        includedMeals: {
          ...base.includedMeals,
          [meal]: !base.includedMeals[meal],
        },
      };
    });
  };

  const handleServesChange = (meal, value) => {
    if (value >= 1 && value <= 12) {
      setPreferences((prev) => {
        const base =
          prev || {
            weekStartDay: 'monday',
            includedMeals: { breakfast: true, lunch: true, dinner: true },
            countryCode: '',
            staplesEnabled: true,
            staples: [],
            defaultServes: { breakfast: 2, lunch: 2, dinner: 4 },
          };
        return {
          ...base,
          defaultServes: {
            ...base.defaultServes,
            [meal]: value,
          },
        };
      });
    }
  };

  const handleStapleAdd = () => {
    const trimmed = newStaple.trim();
    if (!trimmed) {
      setFieldErrors((prev) => ({ ...prev, newStaple: 'Enter a pantry item name.' }));
      focusSummary();
      return;
    }
    setPreferences((prev) => {
      const base = prev || currentPrefs;
      const existing = Array.isArray(base.staples) ? base.staples : [];
      if (existing.some((s) => s.toLowerCase() === trimmed.toLowerCase())) {
        return base;
      }
      return { ...base, staples: [...existing, trimmed] };
    });
    setNewStaple('');
    clearFieldError('newStaple');
  };

  const handleStapleRemove = (value) => {
    setPreferences((prev) => {
      const base = prev || currentPrefs;
      const existing = Array.isArray(base.staples) ? base.staples : [];
      return { ...base, staples: existing.filter((s) => s !== value) };
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`fixed bottom-0 left-0 right-0 bg-card text-foreground rounded-t-3xl z-50 transition-transform duration-300 ease-out max-h-[90vh] overflow-y-auto shadow-[0_-8px_24px_rgba(0,0,0,0.08)] focus:outline-none ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="max-w-md mx-auto">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2 sticky top-0 bg-card z-10">
            <div className="w-10 h-1 bg-muted rounded-full" />
          </div>

          <div className="px-6 pb-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 id={titleId} className="text-xl">Meal Planning Preferences</h2>
                {!isOwner && (
                  <div className="mt-2 rounded-lg border px-3 py-2 text-xs flex items-center gap-2"
                    style={{
                      backgroundColor: '#FCE8EF',
                      borderColor: '#E8B9C9',
                      color: '#B45A7C',
                    }}
                  >
                    <Info className="w-4 h-4" />
                    <span className="font-semibold">Household settings are managed by the owner.</span>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="w-11 h-11 -mr-2 rounded-full hover:bg-gray-50 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {(loadError || saveError || Object.keys(fieldErrors).length > 0) && (
                <div
                  ref={summaryRef}
                  tabIndex={-1}
                  role="alert"
                  aria-live="assertive"
                  className="mpg-focus-ring mb-4 rounded-2xl border border-[#B85A5A]/30 bg-[#FEF5F5] px-4 py-3"
                >
                  <div className="flex items-start gap-2 text-[#B85A5A]">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#B85A5A]" />
                    <div className="space-y-1 text-sm">
                      <p className="font-medium">Please fix the highlighted issue below.</p>
                      {loadError && <p>{loadError}</p>}
                      {saveError && <p>{saveError}</p>}
                      {fieldErrors.newStaple && <p>{fieldErrors.newStaple}</p>}
                    </div>
                  </div>
                </div>
              )}
              {loading && !preferences && (
                <div className="mb-4 text-sm text-muted-foreground">Loading your preferences…</div>
              )}

              {/* Week Start Day */}
              <div className="mb-6">
                <label htmlFor="weekStartDay" className="block text-foreground mb-2">
                  Week Starts On
                </label>
                <p className="mb-3 text-xs text-muted-foreground">
                  You can choose which day your meal plan starts on. This affects how weekly plans and shopping lists are organized.
                </p>
                <select
                  id="weekStartDay"
                  value={currentPrefs.weekStartDay}
                  onChange={(e) => {
                    if (!isOwner) return;
                    setPreferences((prev) => ({
                      ...(prev || currentPrefs),
                      weekStartDay: e.target.value,
                    }));
                  }}
                  disabled={!isOwner}
                  className={`w-full px-4 py-3 bg-input-background rounded-xl border border-[var(--border)] text-foreground ${!isOwner ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  <option value="sunday">Sunday</option>
                  <option value="monday">Monday</option>
                  <option value="tuesday">Tuesday</option>
                  <option value="wednesday">Wednesday</option>
                  <option value="thursday">Thursday</option>
                  <option value="friday">Friday</option>
                  <option value="saturday">Saturday</option>
                </select>
                {weekStartChanged && (
                  <p className="mt-2 text-xs text-[var(--accent-foreground)] bg-[var(--accent-light)] rounded-lg px-3 py-2">
                    This change will remove all existing meals and shopping list items when you return
                    to the Meal Plan and Shopping List screens.
                  </p>
                )}
              </div>

              {/* Meals to include */}
              <div className="mb-6">
                <label className="block text-foreground mb-3">
                  Meals to Include in Plans
                </label>
                <p className="mb-3 text-xs text-muted-foreground">
                  These options control which meals are included when you generate new weekly plans.
                  Changes here do not remove meals from any plans you have already created.
                </p>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 hover:bg-muted rounded-xl transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      checked={currentPrefs.includedMeals.breakfast}
                      onChange={() => isOwner && handleMealToggle('breakfast')}
                      className="w-5 h-5 rounded-md border-2 border-[var(--border)] text-[var(--primary)] accent-[var(--primary)]"
                      disabled={!isOwner}
                    />
                    <span className="text-foreground">Breakfast</span>
                  </label>
                  <label className="flex items-center gap-3 p-3 hover:bg-muted rounded-xl transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      checked={currentPrefs.includedMeals.lunch}
                      onChange={() => isOwner && handleMealToggle('lunch')}
                      className="w-5 h-5 rounded-md border-2 border-[var(--border)] text-[var(--primary)] accent-[var(--primary)]"
                      disabled={!isOwner}
                    />
                    <span className="text-foreground">Lunch</span>
                  </label>
                  <label className="flex items-center gap-3 p-3 hover:bg-muted rounded-xl transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      checked={currentPrefs.includedMeals.dinner}
                      onChange={() => isOwner && handleMealToggle('dinner')}
                      className="w-5 h-5 rounded-md border-2 border-[var(--border)] text-[var(--primary)] accent-[var(--primary)]"
                      disabled={!isOwner}
                    />
                    <span className="text-foreground">Dinner</span>
                  </label>
                </div>
              </div>

              {/* Default Serves */}
              <div className="mb-6">
                <label className="block text-foreground mb-3">
                  Default Number of Servings
                </label>
                <p className="mb-3 text-xs text-muted-foreground">
                  Changes here only apply to meals you generate or add in future weeks. Existing meal plans
                  and shopping lists will keep their current serving sizes.
                </p>
                <div className="space-y-3">
                  {currentPrefs.includedMeals.breakfast && (
                    <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                      <span className="text-foreground">Breakfast</span>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => isOwner && handleServesChange('breakfast', currentPrefs.defaultServes.breakfast - 1)}
                          className="w-8 h-8 rounded-full bg-card border border-[var(--border)] text-foreground flex items-center justify-center hover:bg-[var(--primary-light)] transition-colors disabled:opacity-60"
                          disabled={!isOwner}
                        >
                          -
                        </button>
                        <span className="w-8 text-center">{currentPrefs.defaultServes.breakfast}</span>
                        <button
                          type="button"
                          onClick={() => isOwner && handleServesChange('breakfast', currentPrefs.defaultServes.breakfast + 1)}
                          className="w-8 h-8 rounded-full bg-card border border-[var(--border)] text-foreground flex itemscenter justify-center hover:bg-[var(--primary-light)] transition-colors disabled:opacity-60"
                          disabled={!isOwner}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}

                  {currentPrefs.includedMeals.lunch && (
                    <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                      <span className="text-foreground">Lunch</span>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => isOwner && handleServesChange('lunch', currentPrefs.defaultServes.lunch - 1)}
                          className="w-8 h-8 rounded-full bg-card border border-[var(--border)] text-foreground flex items-center justify-center hover:bg-[var(--primary-light)] transition-colors disabled:opacity-60"
                          disabled={!isOwner}
                        >
                          -
                        </button>
                        <span className="w-8 text-center">{currentPrefs.defaultServes.lunch}</span>
                        <button
                          type="button"
                          onClick={() => isOwner && handleServesChange('lunch', currentPrefs.defaultServes.lunch + 1)}
                          className="w-8 h-8 rounded-full bg-card border border-[var(--border)] text-foreground flex items-center justify-center hover:bg-[var(--primary-light)] transition-colors disabled:opacity-60"
                          disabled={!isOwner}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}

                  {currentPrefs.includedMeals.dinner && (
                    <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                      <span className="text-foreground">Dinner</span>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => isOwner && handleServesChange('dinner', currentPrefs.defaultServes.dinner - 1)}
                          className="w-8 h-8 rounded-full bg-card border border-[var(--border)] text-foreground flex items-center justify-center hover:bg-[var(--primary-light)] transition-colors disabled:opacity-60"
                          disabled={!isOwner}
                        >
                          -
                        </button>
                        <span className="w-8 text-center">{currentPrefs.defaultServes.dinner}</span>
                        <button
                          type="button"
                          onClick={() => isOwner && handleServesChange('dinner', currentPrefs.defaultServes.dinner + 1)}
                          className="w-8 h-8 rounded-full bg-card border border-[var(--border)] text-foreground flex items-center justify-center hoverbg-[var(--primary-light)] transition-colors disabled:opacity-60"
                          disabled={!isOwner}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Country for seasonal picks */}
              <div className="mb-6">
                <label htmlFor="countryCode" className="block text-foreground mb-2">
                  Country (optional)
                </label>
                <p className="mb-3 text-xs text-muted-foreground">
                  Helps us pick meals that fit your current season (e.g., more soups in winter). Start typing to search; we store the country code for accuracy.
                </p>
                <input
                  id="countryCode"
                  type="text"
                  list="country-options"
                  placeholder="Search country (e.g., India)"
                  value={countryInput}
                  onChange={(e) => {
                    if (!isOwner) return;
                    const raw = e.target.value;
                    setCountryInput(raw);
                    const trimmed = raw.trim();
                    const lc = trimmed.toLowerCase();
                    const byCode = countryOptions.find(opt => opt.code.toLowerCase() === lc);
                    const byName = countryOptions.find(opt => opt.name.toLowerCase() === lc);
                    const nextCode = (byCode || byName)?.code || trimmed.toUpperCase();
                    setPreferences((prev) => ({
                      ...(prev || currentPrefs),
                      countryCode: nextCode,
                    }));
                  }}
                  disabled={!isOwner}
                  className={`w-full px-4 py-3 bg-input-background rounded-xl border border-[var(--border)] text-foreground ${!isOwner ? 'opacity-70 cursor-not-allowed' : ''}`}
                />
                <datalist id="country-options">
                  {countryOptions.map((opt) => (
                    <option key={opt.code} value={opt.code} label={opt.name} />
                  ))}
                </datalist>
              </div>

              {/* Staples / Pantry items */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-foreground">Staples / Pantry items</label>
                  <label className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={currentPrefs.staplesEnabled !== false}
                      onChange={(e) =>
                        isOwner && setPreferences((prev) => ({
                          ...(prev || currentPrefs),
                          staplesEnabled: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 rounded-md border-2 border-[var(--border)] text-[var(--primary)] accent-[var(--primary)]"
                      disabled={!isOwner}
                    />
                    <span>Skip staples</span>
                  </label>
                </div>
                <p className="mb-3 text-xs text-muted-foreground">
                  Ingredients that match these names won’t be added to your shopping list. Edit this list anytime.
                </p>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newStaple}
                    onChange={(e) => {
                      if (!isOwner) return;
                      setNewStaple(e.target.value);
                      clearFieldError('newStaple');
                    }}
                    placeholder="Add staple (e.g., water)"
                    aria-invalid={!!fieldErrors.newStaple}
                    aria-describedby={fieldErrors.newStaple ? 'new-staple-error' : undefined}
                    className={`flex-1 px-4 py-3 bg-input-background rounded-xl border border-[var(--border)] text-foreground ${!isOwner ? 'opacity-70 cursor-not-allowed' : ''} ${fieldErrors.newStaple ? 'ring-2 ring-[#B85A5A]' : ''}`}
                    disabled={saving || !isOwner}
                  />
                  <button
                    type="button"
                    onClick={() => isOwner && handleStapleAdd()}
                    className="px-4 py-3 rounded-xl bg-[var(--primary)] text-[var(--primary-foreground)] text-sm font-medium hover:bg-[var(--primary-hover)] transition-colors disabled:opacity-60"
                    disabled={saving || !isOwner}
                  >
                    Add
                  </button>
                </div>
                {fieldErrors.newStaple && (
                  <div id="new-staple-error" className="mb-3 flex items-center gap-1.5 text-sm text-[#B85A5A]">
                    <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
                    <span>{fieldErrors.newStaple}</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {(currentPrefs.staples || []).map((item) => (
                    <span key={item} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--accent-light)] text-sm text-[var(--accent-foreground)]">
                      {item}
                      <button
                        type="button"
                        className="text-[var(--accent-foreground)] hover:text-foreground disabled:opacity-60"
                        onClick={() => isOwner && handleStapleRemove(item)}
                        aria-label={`Remove ${item}`}
                        disabled={!isOwner}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {(currentPrefs.staples || []).length === 0 && (
                    <span className="text-xs text-muted-foreground">No staples yet. Add items to exclude them.</span>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-3 rounded-full border-2 border-[#5F6B3F] text-[#5F6B3F] font-medium bg-white hover:bg-[#F0F3EC] transition-colors"
                >
                  Cancel
                </button>
                {isOwner ? (
                  <button
                    type="submit"
                    className="flex-1 py-3 rounded-full bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary-hover)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : (
                      <span className="inline-flex items-center justify-center gap-2">
                        <Save className="w-4 h-4" />
                        Save Preferences
                      </span>
                    )}
                  </button>
                ) : (
                  <div className="flex-1 py-3 rounded-full bg-muted text-muted-foreground text-center text-sm">
                    Preferences are managed by the household owner
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <ConfirmationDrawer
        isOpen={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={savePreferences}
        title="Update Week Start Day?"
        message="Changing your week start day will clear all existing meals and shopping list items.  You will need to rebuild any current plans. Are you sure you want to save these preferences?"
        confirmLabel="Yes"
      />
    </>
  );
}
